import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  Stack,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Skeleton,
  Menu,
  Modal,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import { ReactComponent as PlusIcon } from "../../../../vendor/_new/icon/sites/plus.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as ArrowupIcon } from "../../../../vendor/_new/icon/sites/arrow_up.svg";
import { ReactComponent as NextlineIcon } from "../../../../vendor/_new/icon/sites/next_line.svg";
import { ReactComponent as ActionIcon } from "../../../../vendor/_new/icon/sites/action.svg";
import { ReactComponent as DeleteIcon } from "../../../../vendor/_new/icon/project/delete.svg";
import { ReactComponent as MoveIcon } from "../../../../vendor/_new/icon/project/move.svg";
import { ReactComponent as RenameIcon } from "../../../../vendor/_new/icon/project/rename.svg";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as ProjectIcon } from "../../../../vendor/_new/icon/navbar/project.svg";
import { ReactComponent as MovesiteIcon } from "../../../../vendor/_new/icon/sites/manage_domain.svg";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import PermissionGate from "../../../guards/PermissionGate";
import { containedButtonSx } from "../../../common/Button";
import { UserType } from "../../../../utils/constants";
import { formatDate } from "../../../../utils/format";
import { getStatusLabel } from "../sites";
import { getEnforcementLabel } from "../sites";
import ProjectModal from "./ProjectModal";
import ProjectMoveModal from "./ProjectMoveModal";
import SiteMoveModal from "./SiteMoveModal";
import AddSiteModal from "./AddSiteModal";
import ProjectModalToAddNewProject from "./ProjectModalToAddNewProject";
import ProjectMembersDrawer from "./ProjectMembersDrawer";
import { tableHeadSx, tableBodySx } from "../detection/WAFEvent";
import { generateMenuSx } from "../../../../layouts/_new/Normal";
import { CancelButton, SaveButton } from "../../../common/Button";
import useSite from "../../../../hooks/user/useSite";
import useAuth from "../../../../hooks/useAuth";
import useHome from "../../../../hooks/user/useHome";
import usePermissions from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../utils/permissions";

const SkeletonContent = (props) => {
  const { rowsPerPage } = props;

  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  var cellArray = [0, 1, 2, 3];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{
                padding: "16px 8px",
              }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{
                  borderRadius: "11px",
                }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
};

const isActiveProjectSite = (site) => site && site.isDeleted !== true && !site.deleted;

const filterActiveSites = (sites) => (sites || []).filter(isActiveProjectSite);

const filterProjectListSites = (projects) =>
  (projects || []).map((project) => ({
    ...project,
    sites: filterActiveSites(project.sites),
    subprojects: (project.subprojects || []).map((subproject) => ({
      ...subproject,
      sites: filterActiveSites(subproject.sites),
    })),
  }));

const countSubprojectSites = (subproject) => subproject?.sites?.length || 0;

const countProjectSites = (project) =>
  (project?.sites?.length || 0) +
  (project?.subprojects || []).reduce((total, subproject) => total + countSubprojectSites(subproject), 0);

const PROJECT_TABLE_COL_COUNT = 4;

const SiteNestedTableHead = () => (
  <TableHead>
    <TableRow>
      <TableCell>
        <Typography sx={tableHeadSx}>Domain</Typography>
      </TableCell>
      <TableCell>
        <Typography sx={tableHeadSx}>Origin</Typography>
      </TableCell>
      <TableCell>
        <Typography sx={tableHeadSx}>On boarded</Typography>
      </TableCell>
      <TableCell>
        <Typography sx={tableHeadSx}>Status</Typography>
      </TableCell>
      <TableCell>
        <Typography sx={tableHeadSx}>Enforcement</Typography>
      </TableCell>
      <TableCell align="right" sx={{ width: 80 }}>
        <Typography sx={tableHeadSx}>Action</Typography>
      </TableCell>
    </TableRow>
  </TableHead>
);

const NewProjectSettingsPage = () => {
  const { userType } = useAuth();
  const { getAllProjectList, deleteProject, deleteSiteFromProject } = useSite();
  const { getAllProjectBaseList } = useHome();
  const { hasPermission, PERMISSIONS: PERMS } = usePermissions();
  
  // Check if user has permission to view projects

  const [openProjectModal, setOpenProjectModal] = useState(false);
  const handleProjectModalOpen = () => setOpenProjectModal(true);
  const handleProjectModalClose = () => setOpenProjectModal(false);

  const [openProjectToAddNewProjectModal, setOpenProjectToAddNewProjectModal] = useState(false);
  const handleProjectToAddNewProjectModalOpen = () => setOpenProjectToAddNewProjectModal(true);
  const handleProjectToAddNewProjectModalClose = () => setOpenProjectToAddNewProjectModal(false);

  const [openMoveProjectModal, setOpenMoveProjectModal] = useState(false);
  const handleMoveProjectModalOpen = () => setOpenMoveProjectModal(true);
  const handleMoveProjectModalClose = () => setOpenMoveProjectModal(false);

  const [projectToEdit, setProjectToEdit] = useState(null);

  const [projectList, setProjectList] = useState(null);
  const [nonProjectSites, setNonProjectSites] = useState(null);

  const getProjectList = useCallback(async () => {
    const res = await getAllProjectList();
    setProjectList(filterProjectListSites(res?.projects));
    setNonProjectSites(filterActiveSites(res?.sites));
    // Refresh global project selector cache without repainting this page from basis_all
    // (basis_all.sites is all org sites; /project.sites is unassigned-only — mixing them caused count flicker)
    getAllProjectBaseList().catch(() => {});
  }, [getAllProjectBaseList, getAllProjectList]);

  useEffect(() => {
    getProjectList();
  }, [getProjectList]);

  const [openIndex, setOpenIndex] = useState(null);
  const [openSubIndex, setOpenSubIndex] = useState(null);

  const handleClick = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleSubClick = (index) => {
    setOpenSubIndex(openSubIndex === index ? null : index);
  };

  const [actionProjectMenu, setActionProjectMenu] = useState({
    anchorEl: null,
    projectId: null,
  });
  const handleActionProjectMenuOpen = (event, projectId) => {
    event.stopPropagation();
    setActionProjectMenu({ anchorEl: event.currentTarget, projectId });
  };
  const handleActionProjectMenuClose = () => {
    setActionProjectMenu({ anchorEl: null, projectId: null });
  };
  const isActionProjectMenuOpen = Boolean(actionProjectMenu.anchorEl);

  const [actionProjectMainMenu, setActionProjectMainMenu] = useState({
    anchorEl: null,
    projectId: null,
  });
  const handleActionProjectMainMenuOpen = (event, projectId) => {
    event.stopPropagation();
    setActionProjectMainMenu({ anchorEl: event.currentTarget, projectId });
  };
  const handleActionProjectMainMenuClose = () => {
    setActionProjectMainMenu({ anchorEl: null, projectId: null });
  };
  const isActionProjectMainMenuOpen = Boolean(actionProjectMainMenu.anchorEl);

  const [projectToDelete, setProjectToDelete] = useState(null);
  const [openProjectToDelete, setOpenProjectToDelete] = useState(false);
  const handleProjectToDeleteOpen = () => setOpenProjectToDelete(true);
  const handleProjectToDeleteClose = () => setOpenProjectToDelete(false);

  const [loading, setLoading] = useState(false);
  const handleDeleteProject = async () => {
    if (!hasPermission(PERMISSIONS.PROJECTS_DELETE)) return;
    if (!projectToDelete) return;
    setLoading(true);
    await deleteProject(projectToDelete?.id);
    await getProjectList();
    setLoading(false);
    handleProjectToDeleteClose();
  };

  const [actionSiteMenu, setActionSiteMenu] = useState({
    anchorEl: null,
    projectId: null,
    siteId: null,
  });
  const handleActionSiteMenuOpen = (event, projectId, siteId) => {
    event.stopPropagation();
    setActionSiteMenu({ anchorEl: event.currentTarget, projectId, siteId });
  };
  const handleActionSiteMenuClose = () => {
    setActionSiteMenu({ anchorEl: null, projectId: null, siteId: null });
  };
  const isActionSiteMenuOpen = Boolean(actionSiteMenu.anchorEl);

  const [actionNonSiteMenu, setActionNonSiteMenu] = useState({
    anchorEl: null,
    projectId: null,
    siteId: null,
  });
  const handleActionNonSiteMenuOpen = (event, projectId, siteId) => {
    event.stopPropagation();
    setActionNonSiteMenu({ anchorEl: event.currentTarget, projectId, siteId });
  };
  const handleActionNonSiteMenuClose = () => {
    setActionNonSiteMenu({ anchorEl: null, projectId: null, siteId: null });
  };
  const isActionNonSiteMenuOpen = Boolean(actionNonSiteMenu.anchorEl);

  const [projectIdForSelectedSite, setProjectIdForSelectedSite] = useState(null);
  const [siteToMove, setSiteToMove] = useState(null);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [openSiteToDelete, setOpenSiteToDelete] = useState(false);
  const handleSiteToDeleteOpen = () => setOpenSiteToDelete(true);
  const handleSiteToDeleteClose = () => setOpenSiteToDelete(false);

  const [openMoveSiteModal, setOpenMoveSiteModal] = useState(false);
  const handleMoveSiteModalOpen = () => setOpenMoveSiteModal(true);
  const handleMoveSiteModalClose = () => setOpenMoveSiteModal(false);

  const [projectIdToAddSite, setProjectIdToAddSite] = useState(null);
  const [openAddSiteModal, setOpenAddSiteModal] = useState(false);
  const handleAddSiteModalOpen = () => setOpenAddSiteModal(true);
  const handleAddSiteModalClose = () => setOpenAddSiteModal(false);

  const [projectForMembers, setProjectForMembers] = useState(null);
  const [openMembersModal, setOpenMembersModal] = useState(false);
  const handleMembersModalOpen = () => setOpenMembersModal(true);
  const handleMembersModalClose = () => {
    setOpenMembersModal(false);
    setProjectForMembers(null);
  };

  const handleDeleteSite = async () => {
    if (!hasPermission(PERMISSIONS.SITES_DELETE)) return;
    if (!siteToDelete) return;
    setLoading(true);
    await deleteSiteFromProject(siteToDelete?.id);
    await getProjectList();
    setLoading(false);
    handleSiteToDeleteClose();
  };

  const allProjects = useMemo(() => {
    return projectList?.flatMap((p) => [p, ...(p.subprojects || [])]);
  }, [projectList]);

  const allSites = useMemo(() => {
    return nonProjectSites?.concat(
      projectList?.flatMap((p) => [...(p?.sites || []), ...(p?.subprojects || []).flatMap((sp) => sp?.sites || [])])
    );
  }, [projectList, nonProjectSites]);

  return (
    <PermissionGate permission={PERMS.PROJECTS_READ} resourceLabel="projects">
    <>
      <Helmet title="Projects" />
      <Stack spacing={4} sx={{ p: 6 }}>
        <V2PageHeader
          title="Project Settings"
          sx={{ pb: 0, alignItems: "center" }}
          actions={
            hasPermission(PERMISSIONS.PROJECTS_WRITE) ? (
              <Button
                onClick={() => {
                  handleProjectModalOpen();
                  setProjectToEdit(null);
                }}
                startIcon={<PlusIcon style={{ color: colors.neutral[100] }} />}
                sx={{ height: 32, minWidth: 140, ...containedButtonSx }}
              >
                Add project
              </Button>
            ) : null
          }
        />
        <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
          <Table
            sx={{
              "& .MuiTableCell-head": {
                py: 2,
                px: 3,
                backgroundColor: colors.neutral[50],
                borderBottom: `1px solid ${colors.neutral[200]}`,
              },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography sx={tableHeadSx}>Project name</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Sites</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Created</Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: 80 }}>
                  <Typography sx={tableHeadSx}>Action</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            {projectList === null && <SkeletonContent rowsPerPage={5} />}
            <TableBody>
              {projectList?.map((project) => (
                <React.Fragment key={`project_${project.id}`}>
                  <TableRow
                    sx={{ "& .MuiTableCell-root": { backgroundColor: openIndex === project?.id ? colors.primary["alpha"] : "inherit" } }}
                  >
                  <TableCell sx={{ borderTopLeftRadius: openIndex === project?.id ? "8px" : "0px" }}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <ProjectIcon style={{ color: openIndex === project?.id ? colors.primary[300] : colors.neutral[600] }} />
                      <Typography
                        sx={{
                          ...tableBodySx,
                          color: openIndex === project?.id ? colors.primary[300] : colors.neutral[800],
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: `calc(24vw - 150px)`,
                        }}
                      >
                        {project?.name}
                      </Typography>
                      {project?.subprojects?.length + project.sites?.length > 0 && (
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          onClick={() => handleClick(project?.id)}
                          sx={{ cursor: "pointer", width: 24, height: 24 }}
                        >
                          {openIndex === project?.id ? (
                            <ArrowupIcon style={{ color: colors.primary[300] }} />
                          ) : (
                            <ArrowdownIcon style={{ color: colors.neutral[600] }} />
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableBodySx}>{countProjectSites(project)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableBodySx}>{formatDate(project?.created_date) || "—"}</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "5.6%", borderTopRightRadius: openIndex === project?.id ? "8px" : "0px" }}>
                    {userType !== UserType.ORG_USER && (
                      <Stack alignItems={"flex-end"} width={"100%"}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          onClick={(e) => handleActionProjectMainMenuOpen(e, project?.id)}
                          sx={{ cursor: "pointer", width: 24, height: 24 }}
                        >
                          <ActionIcon style={{ color: colors.neutral[600] }} />
                        </Stack>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={PROJECT_TABLE_COL_COUNT} sx={{ p: `0px!important` }}>
                    <Collapse in={openIndex === project.id} timeout="auto" unmountOnExit>
                      <Table
                        sx={{
                          "& .MuiTableCell-head": { py: 3, px: 3 },
                          "& .MuiTableCell-body": { py: 2, px: 3 },
                          "& .MuiTableCell-root": { borderBottom: "none" },
                        }}
                      >
                        <TableBody>
                          {project.subprojects.map((subproject, subIndex) => (
                            <React.Fragment key={`subproject_${subproject.id}`}>
                              <TableRow
                              key={`site_${project.id}_${subproject.name}`}
                              sx={{
                                "& .MuiTableCell-root": {
                                  backgroundColor: openIndex === project.id ? colors.primary["alpha"] : "inherit",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  borderBottomLeftRadius:
                                    subIndex === project.subprojects.length - 1 &&
                                    project?.sites?.length === 0 &&
                                    subproject?.sites?.length === 0
                                      ? "8px"
                                      : "0px",
                                }}
                              >
                                <Stack direction={"row"} alignItems={"center"} spacing={1} sx={{ pl: 7 }}>
                                  <ProjectIcon style={{ color: openIndex === project?.id ? colors.primary[300] : colors.neutral[600] }} />
                                  <Typography
                                    sx={{
                                      ...tableBodySx,
                                      color: openIndex === project?.id ? colors.primary[300] : colors.neutral[800],
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: `calc(24vw - 150px)`,
                                    }}
                                  >
                                    {subproject?.name}
                                  </Typography>
                                  {subproject.sites?.length > 0 && (
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      justifyContent={"center"}
                                      onClick={() => handleSubClick(subproject?.id)}
                                      sx={{ cursor: "pointer", width: 24, height: 24 }}
                                    >
                                      {openSubIndex === subproject?.id ? (
                                        <ArrowupIcon style={{ color: colors.primary[300] }} />
                                      ) : (
                                        <ArrowdownIcon style={{ color: colors.primary[300] }} />
                                      )}
                                    </Stack>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography sx={tableBodySx}>{countSubprojectSites(subproject)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={tableBodySx}>{formatDate(subproject?.created_date) || "—"}</Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  width: "5.6%",
                                  borderBottomRightRadius:
                                    subIndex === project.subprojects.length - 1 &&
                                    project?.sites?.length === 0 &&
                                    subproject?.sites?.length === 0
                                      ? "8px"
                                      : "0px",
                                }}
                              >
                                {userType !== UserType.ORG_USER && (
                                  <Stack alignItems={"flex-end"} width={"100%"}>
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      justifyContent={"center"}
                                      onClick={(e) => handleActionProjectMenuOpen(e, subproject?.id)}
                                      sx={{ cursor: "pointer", width: 24, height: 24 }}
                                    >
                                      <ActionIcon style={{ color: colors.neutral[600] }} />
                                    </Stack>
                                  </Stack>
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={PROJECT_TABLE_COL_COUNT} sx={{ p: `0px!important` }}>
                                <Collapse in={openSubIndex === subproject.id} timeout="auto" unmountOnExit>
                                  <Table
                                    sx={{
                                      "& .MuiTableCell-head": { py: 3, px: 3 },
                                      "& .MuiTableCell-body": { py: 2, px: 3 },
                                      "& .MuiTableCell-root": { borderBottom: "none" },
                                    }}
                                  >
                                    <SiteNestedTableHead />
                                    <TableBody>
                                      {subproject?.sites?.map((site, index2) => (
                                        <TableRow
                                          key={`site_${site.id}`}
                                          sx={{
                                            "& .MuiTableCell-root": {
                                              backgroundColor: openIndex === project?.id ? colors.primary["alpha"] : "inherit",
                                            },
                                          }}
                                        >
                                          <TableCell
                                            sx={{
                                              width: "20.4%",
                                              borderBottomLeftRadius:
                                                subIndex === project.subprojects.length - 1 &&
                                                project?.sites?.length === 0 &&
                                                index2 === subproject?.sites?.length - 1
                                                  ? "8px"
                                                  : "0px",
                                            }}
                                          >
                                            <Stack direction={"row"} alignItems={"center"} spacing={1} sx={{ pl: 14 }}>
                                              <Stack sx={{ width: 24, height: 24 }}>
                                                <NextlineIcon style={{ color: colors.neutral[600] }} />
                                              </Stack>
                                              <Typography sx={tableBodySx}>{site.site_id}</Typography>
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={{ width: "10.2%" }}>
                                            <Typography sx={tableBodySx}>{site.addr}</Typography>
                                          </TableCell>
                                          <TableCell sx={{ width: "16.2%" }}>
                                            <Typography sx={tableBodySx}>{formatDate(site?.created_date) || "—"}</Typography>
                                          </TableCell>
                                          <TableCell sx={{ width: "11.1%" }}>{getStatusLabel(site?.isDeleted)}</TableCell>
                                          <TableCell sx={{ width: "11.1%" }}>
                                            {getEnforcementLabel(site.status) ?? (
                                              <Skeleton
                                                height="22px"
                                                width="100%"
                                                py="5px"
                                                variant="rectangular"
                                                sx={{ borderRadius: "11px" }}
                                              />
                                            )}
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              width: "5.6%",
                                              borderBottomRightRadius:
                                                subIndex === project.subprojects.length - 1 &&
                                                project?.sites?.length === 0 &&
                                                index2 === subproject?.sites?.length - 1
                                                  ? "8px"
                                                  : "0px",
                                            }}
                                          >
                                            {userType !== UserType.ORG_USER && (
                                              <Stack alignItems={"flex-end"} width={"100%"}>
                                                <Stack
                                                  direction={"row"}
                                                  alignItems={"center"}
                                                  justifyContent={"center"}
                                                  onClick={(e) => handleActionSiteMenuOpen(e, subproject?.id, site?.id)}
                                                  sx={{ cursor: "pointer", width: 24, height: 24 }}
                                                >
                                                  <ActionIcon style={{ color: colors.neutral[600] }} />
                                                </Stack>
                                              </Stack>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={PROJECT_TABLE_COL_COUNT} sx={{ p: `0px!important` }}>
                    <Collapse in={openIndex === project?.id} timeout="auto" unmountOnExit>
                      <Table
                        sx={{
                          "& .MuiTableCell-head": { py: 3, px: 3 },
                          "& .MuiTableCell-body": { py: 2, px: 3 },
                          "& .MuiTableCell-root": { borderBottom: "none" },
                        }}
                      >
                        <SiteNestedTableHead />
                        <TableBody>
                          {project?.sites?.map((site, index1) => (
                            <TableRow
                              key={`site_${site.id}`}
                              sx={{
                                "& .MuiTableCell-root": {
                                  backgroundColor: openIndex === project?.id ? colors.primary["alpha"] : "inherit",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  width: "20.4%",
                                  borderBottomLeftRadius: index1 === project?.sites?.length - 1 ? "8px" : "0px",
                                }}
                              >
                                <Stack direction={"row"} alignItems={"center"} spacing={1} sx={{ pl: 7 }}>
                                  <Stack sx={{ width: 24, height: 24 }}>
                                    <NextlineIcon style={{ color: colors.neutral[600] }} />
                                  </Stack>
                                  <Typography sx={tableBodySx}>{site.site_id}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ width: "10.2%" }}>
                                <Typography sx={tableBodySx}>{site.addr}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: "16.2%" }}>
                                <Typography sx={tableBodySx}>{formatDate(site?.created_date) || "—"}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: "11.1%" }}>{getStatusLabel(site?.isDeleted)}</TableCell>
                              <TableCell sx={{ width: "11.1%" }}>
                                {getEnforcementLabel(site.status) ?? (
                                  <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  width: "5.6%",
                                  borderBottomRightRadius: index1 === project?.sites?.length - 1 ? "8px" : "0px",
                                }}
                              >
                                {userType !== UserType.ORG_USER && (
                                  <Stack alignItems={"flex-end"} width={"100%"}>
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      justifyContent={"center"}
                                      onClick={(e) => handleActionSiteMenuOpen(e, project?.id, site?.id)}
                                      sx={{ cursor: "pointer", width: 24, height: 24 }}
                                    >
                                      <ActionIcon style={{ color: colors.neutral[600] }} />
                                    </Stack>
                                  </Stack>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              ))}
              {nonProjectSites?.length > 0 && (
                <React.Fragment key="non_project_sites">
                <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: openIndex === 0 ? colors.primary["alpha"] : "inherit" } }}>
                  <TableCell sx={{ borderTopLeftRadius: openIndex === 0 ? "8px" : "0px" }}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <ProjectIcon style={{ color: openIndex === 0 ? colors.primary[300] : colors.neutral[600] }} />
                      <Typography
                        sx={{
                          ...tableBodySx,
                          color: openIndex === 0 ? colors.primary[300] : colors.neutral[800],
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: `calc(24vw - 150px)`,
                        }}
                      >
                        Default
                      </Typography>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        onClick={() => handleClick(0)}
                        sx={{ cursor: "pointer", width: 24, height: 24 }}
                      >
                        {openIndex === 0 ? (
                          <ArrowupIcon style={{ color: colors.primary[300] }} />
                        ) : (
                          <ArrowdownIcon style={{ color: colors.neutral[600] }} />
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableBodySx}>{nonProjectSites?.length || 0}</Typography>
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ borderTopRightRadius: openIndex === 0 ? "8px" : "0px" }} />
                </TableRow>
                <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      backgroundColor: openIndex === 0 ? colors.primary["alpha"] : "inherit",
                      borderBottomRightRadius: "8px",
                      borderBottomLeftRadius: "8px",
                    },
                  }}
                >
                  <TableCell colSpan={PROJECT_TABLE_COL_COUNT} sx={{ p: `0px!important` }}>
                    <Collapse in={openIndex === 0} timeout="auto" unmountOnExit>
                      <Table
                        sx={{
                          "& .MuiTableCell-head": { py: 3, px: 3 },
                          "& .MuiTableCell-body": { py: 2, px: 3 },
                          "& .MuiTableCell-root": { borderBottom: "none" },
                        }}
                      >
                        <SiteNestedTableHead />
                        <TableBody>
                          {nonProjectSites?.map((site) => (
                            <TableRow
                              key={`site_${site.id}`}
                              sx={{
                                "& .MuiTableCell-root": {
                                  backgroundColor: openIndex === site.id ? colors.primary["alpha"] : "inherit",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  width: "20.4%",
                                }}
                              >
                                <Stack direction={"row"} alignItems={"center"} spacing={1} sx={{ pl: 7 }}>
                                  <Stack sx={{ width: 24, height: 24 }}>
                                    <NextlineIcon style={{ color: colors.neutral[600] }} />
                                  </Stack>
                                  <Typography sx={tableBodySx}>{site.site_id}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ width: "10.2%" }}>
                                <Typography sx={tableBodySx}>{site.addr}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: "16.2%" }}>
                                <Typography sx={tableBodySx}>{formatDate(site?.created_date) || "—"}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: "11.1%" }}>{getStatusLabel(site?.isDeleted)}</TableCell>
                              <TableCell sx={{ width: "11.1%" }}>
                                {getEnforcementLabel(site.status) ?? (
                                  <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  width: "5.6%",
                                }}
                              >
                                {userType !== UserType.ORG_USER && (
                                  <Stack alignItems={"flex-end"} width={"100%"}>
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      justifyContent={"center"}
                                      onClick={(e) => handleActionNonSiteMenuOpen(e, 0, site?.id)}
                                      sx={{ cursor: "pointer", width: 24, height: 24 }}
                                    >
                                      <ActionIcon style={{ color: colors.neutral[600] }} />
                                    </Stack>
                                  </Stack>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              )}
            </TableBody>
          </Table>
        </Box>
      </Stack>
      <Menu
        anchorEl={actionProjectMenu.anchorEl}
        open={isActionProjectMenuOpen}
        onClose={handleActionProjectMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 144,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMenu;
              setProjectToEdit(allProjects?.find((project) => project?.id === projectId));
              handleActionProjectMenuClose();
              handleProjectModalOpen();
            }}
            sx={generateMenuSx(120, 37)}
          >
            <RenameIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Edit
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMenu;
              setProjectToEdit(allProjects?.find((project) => project?.id === projectId));
              handleActionProjectMenuClose();
              handleMoveProjectModalOpen();
            }}
            sx={generateMenuSx(120, 37)}
          >
            <MoveIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Move</Typography>
          </Stack>
          {hasPermission(PERMISSIONS.PROJECTS_DELETE) && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => {
                const { projectId } = actionProjectMenu;
                setProjectToDelete(allProjects?.find((project) => project?.id === projectId));
                handleActionProjectMenuClose();
                handleProjectToDeleteOpen();
              }}
              sx={generateMenuSx(120, 37)}
            >
              <DeleteIcon />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                Delete
              </Typography>
            </Stack>
          )}
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMenu;
              setProjectForMembers(allProjects?.find((project) => project?.id === projectId));
              handleActionProjectMenuClose();
              handleMembersModalOpen();
            }}
            sx={generateMenuSx(120, 37)}
          >
            <GroupIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Members
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMenu;
              setProjectIdToAddSite(projectId);
              handleActionProjectMenuClose();
              handleAddSiteModalOpen();
            }}
            sx={generateMenuSx(120, 37)}
          >
            <MovesiteIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Add site
            </Typography>
          </Stack>
        </Box>
      </Menu>
      <Menu
        anchorEl={actionProjectMainMenu.anchorEl}
        open={isActionProjectMainMenuOpen}
        onClose={handleActionProjectMainMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 164,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMainMenu;
              setProjectToEdit(allProjects?.find((project) => project?.id === projectId));
              handleActionProjectMainMenuClose();
              handleProjectModalOpen();
            }}
            sx={generateMenuSx(140, 37)}
          >
            <RenameIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Edit
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMainMenu;
              setProjectToEdit(allProjects?.find((project) => project?.id === projectId));
              handleActionProjectMainMenuClose();
              handleMoveProjectModalOpen();
            }}
            sx={generateMenuSx(140, 37)}
          >
            <MoveIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Move</Typography>
          </Stack>
          {hasPermission(PERMISSIONS.PROJECTS_DELETE) && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => {
                const { projectId } = actionProjectMainMenu;
                setProjectToDelete(allProjects?.find((project) => project?.id === projectId));
                handleActionProjectMainMenuClose();
                handleProjectToDeleteOpen();
              }}
              sx={generateMenuSx(140, 37)}
            >
              <DeleteIcon />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                Delete
              </Typography>
            </Stack>
          )}
          {hasPermission(PERMISSIONS.PROJECTS_WRITE) && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => {
                const { projectId } = actionProjectMainMenu;
                setProjectToEdit(allProjects?.find((project) => project?.id === projectId));
                handleActionProjectMainMenuClose();
                handleProjectToAddNewProjectModalOpen();
              }}
              sx={generateMenuSx(140, 37)}
            >
              <ProjectIcon style={{ width: 24, height: 24 }} />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                Add project
              </Typography>
            </Stack>
          )}
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMainMenu;
              setProjectForMembers(allProjects?.find((project) => project?.id === projectId));
              handleActionProjectMainMenuClose();
              handleMembersModalOpen();
            }}
            sx={generateMenuSx(140, 37)}
          >
            <GroupIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Members
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId } = actionProjectMainMenu;
              setProjectIdToAddSite(projectId);
              handleActionProjectMainMenuClose();
              handleAddSiteModalOpen();
            }}
            sx={generateMenuSx(140, 37)}
          >
            <MovesiteIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Add site
            </Typography>
          </Stack>
        </Box>
      </Menu>
      <Menu
        anchorEl={actionSiteMenu.anchorEl}
        open={isActionSiteMenuOpen}
        onClose={handleActionSiteMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 144,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId, siteId } = actionSiteMenu;
              setSiteToMove(allSites?.find((site) => site?.id === siteId));
              setProjectIdForSelectedSite(projectId);
              handleActionSiteMenuClose();
              handleMoveSiteModalOpen();
            }}
            sx={generateMenuSx(120, 37)}
          >
            <MoveIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Move</Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { siteId } = actionSiteMenu;
              setSiteToDelete(allSites?.find((site) => site?.id === siteId));
              handleActionSiteMenuClose();
              handleSiteToDeleteOpen();
            }}
            sx={generateMenuSx(120, 37)}
          >
            <DeleteIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Delete
            </Typography>
          </Stack>
        </Box>
      </Menu>
      <Menu
        anchorEl={actionNonSiteMenu.anchorEl}
        open={isActionNonSiteMenuOpen}
        onClose={handleActionNonSiteMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 144,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { projectId, siteId } = actionNonSiteMenu;
              setSiteToMove(allSites?.find((site) => site?.id === siteId));
              setProjectIdForSelectedSite(projectId);
              handleActionNonSiteMenuClose();
              handleMoveSiteModalOpen();
            }}
            sx={generateMenuSx(120, 37)}
          >
            <MoveIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Move</Typography>
          </Stack>
        </Box>
      </Menu>
      <Modal open={openProjectToDelete} onClose={handleProjectToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Project?
            </Typography>
            <Box onClick={handleProjectToDeleteClose} sx={{ cursor: "pointer" }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            You're about to permanently delete the project{" "}
            <span style={{ color: colors.neutral[1000] }}>
              <b>{projectToDelete?.name}</b>
            </span>{" "}
            along with its configurations.
          </Typography>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            This action cannot be undone.
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleProjectToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDeleteProject} disabled={loading} label="Delete" />
          </Stack>
        </Stack>
      </Modal>
      <Modal open={openSiteToDelete} onClose={handleSiteToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Site?
            </Typography>
            <Box onClick={handleSiteToDeleteClose} sx={{ cursor: "pointer" }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            You're about to delete the site{" "}
            <span style={{ color: colors.neutral[1000] }}>
              <b>{siteToDelete?.site_id}</b>
            </span>{" "}
            along with its configurations.
          </Typography>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            This action cannot be undone.
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleSiteToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDeleteSite} disabled={loading} label="Delete" />
          </Stack>
        </Stack>
      </Modal>
      <ProjectModal
        open={openProjectModal}
        handleClose={handleProjectModalClose}
        projectToEdit={projectToEdit}
        getProjectList={getProjectList}
      />
      <ProjectModalToAddNewProject
        open={openProjectToAddNewProjectModal}
        handleClose={handleProjectToAddNewProjectModalClose}
        targetProject={projectToEdit}
        getProjectList={getProjectList}
      />
      <ProjectMoveModal
        open={openMoveProjectModal}
        handleClose={handleMoveProjectModalClose}
        projectToMove={projectToEdit}
        getProjectList={getProjectList}
        allProjects={allProjects}
      />
      <SiteMoveModal
        open={openMoveSiteModal}
        handleClose={handleMoveSiteModalClose}
        siteToMove={siteToMove}
        getProjectList={getProjectList}
        allProjects={allProjects}
        projectIdForSelectedSite={projectIdForSelectedSite}
      />
      <AddSiteModal
        open={openAddSiteModal}
        handleClose={handleAddSiteModalClose}
        getProjectList={getProjectList}
        sites={nonProjectSites}
        projectId={projectIdToAddSite}
      />
      <ProjectMembersDrawer
        open={openMembersModal}
        onClose={handleMembersModalClose}
        project={projectForMembers}
      />
    </>
    </PermissionGate>
  );
};
export default NewProjectSettingsPage;
